import { doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db } from "./firebase";

export function normalizeUsername(input: string) {
  return input.trim().toLowerCase();
}

export function isValidUsername(input: string) {
  const u = normalizeUsername(input);
  return /^[a-z0-9_]{3,16}$/.test(u);
}

/**
 * Reserve a unique username:
 * - creates usernames/{newU}
 * - deletes usernames/{oldU} if owned
 * - updates users/{uid}.username
 * - updates publicProfiles/{uid}.username when a public profile already exists
 */
export async function setUniqueUsername(params: {
  uid: string;
  newUsername: string;
  oldUsername?: string | null;
}) {
  const newU = normalizeUsername(params.newUsername);
  const oldU = params.oldUsername ? normalizeUsername(params.oldUsername) : null;

  if (!isValidUsername(newU)) {
    throw new Error("Nom d'utilisateur invalide. Utilise 3 a 16 caracteres : a-z, 0-9, _");
  }

  if (oldU === newU) return;

  const newRef = doc(db, "usernames", newU);
  const oldRef = oldU ? doc(db, "usernames", oldU) : null;
  const userRef = doc(db, "users", params.uid);
  const publicProfileRef = doc(db, "publicProfiles", params.uid);

  await runTransaction(db, async (tx) => {
    const newSnap = await tx.get(newRef);
    const publicProfileSnap = await tx.get(publicProfileRef);
    if (newSnap.exists()) {
      throw new Error("Nom d'utilisateur deja pris");
    }

    // create new reservation
    tx.set(newRef, {
      uid: params.uid,
      createdAt: serverTimestamp(),
    });

    // delete old reservation if owned
    if (oldRef) {
      const oldSnap = await tx.get(oldRef);
      if (oldSnap.exists()) {
        const data = oldSnap.data() as any;
        if (data?.uid === params.uid) {
          tx.delete(oldRef);
        }
      }
    }

    // update user doc
    tx.set(
      userRef,
      {
        username: newU,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    if (publicProfileSnap.exists()) {
      tx.set(
        publicProfileRef,
        {
          uid: params.uid,
          username: newU,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }
  });
}
